import { useEffect, useState, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { isPlanRequired } from "../lib/analytics";
import { isFreeQuotaExceeded } from "../lib/upsell";
import { StandardPaywallDialog } from "../components/StandardUpsell";
import Layout from "../components/Layout";
import { useGeo, MonitoredAreas } from "../components/RadarGeo";
import OfferDeadlineBadge from "../components/OfferDeadline";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import {
    Plus, MapPin, ArrowRight, Lightning, Trophy, Sparkle, Ruler,
    CaretDown, CaretUp, Buildings, Clock, CheckCircle, WarningCircle, Gauge,
    ChartLineUp, Brain, Scales, Broadcast, Archive as ArchiveIcon, Fire, CaretRight,
} from "@phosphor-icons/react";

function scoreTone(score) {
    if (score == null) return { bg: "bg-white/[0.06]", text: "text-slate-300", ring: "#8B99B0" };
    if (score <= 40) return { bg: "bg-red-50", text: "text-red-700", ring: "#F87171" };
    if (score <= 60) return { bg: "bg-orange-50", text: "text-orange-700", ring: "#FB923C" };
    if (score <= 80) return { bg: "bg-yellow-50", text: "text-yellow-800", ring: "#FACC15" };
    return { bg: "bg-emerald-50", text: "text-emerald-700", ring: "#4ADE80" };
}

function scoreColor(score) {
    if (score == null) return { bg: "bg-white/[0.06]", text: "text-slate-300", label: "—" };
    if (score <= 40) return { bg: "bg-red-50", text: "text-red-700", label: "SCARTA" };
    if (score <= 60) return { bg: "bg-orange-50", text: "text-orange-700", label: "ALTO RISCHIO" };
    if (score <= 80) return { bg: "bg-yellow-50", text: "text-yellow-800", label: "INTERESSANTE" };
    return { bg: "bg-emerald-50", text: "text-emerald-700", label: "OTTIMA" };
}

function scoreWord(score) {
    if (score == null) return { word: "—", color: "#8B99B0" };
    if (score <= 40) return { word: "Debole", color: "var(--acc-red)" };
    if (score <= 60) return { word: "Alto rischio", color: "var(--acc-orange)" };
    if (score <= 80) return { word: "Buona opportunità", color: "var(--acc-green)" };
    return { word: "Ottima opportunità", color: "var(--acc-green)" };
}

function statusBadge(status) {
    const map = {
        draft: { text: "Bozza", icon: Clock, cls: "border-gray-300 text-gray-700" },
        analyzing: { text: "Analisi in corso", icon: Clock, cls: "border-blue-300 bg-blue-50 text-blue-700" },
        completed: { text: "Completata", icon: CheckCircle, cls: "border-emerald-300 bg-emerald-50 text-emerald-700" },
        error: { text: "Errore", icon: WarningCircle, cls: "border-red-300 bg-red-50 text-red-700" },
    };
    return map[status] || map.draft;
}

const fmtEur = (v) => (v == null ? "—" : "€ " + Number(v).toLocaleString("it-IT", { maximumFractionDigits: 0 }));
const propLabel = (t) => (t || "Immobile").replace(/_/g, " ");

function CountUp({ value, className, style }) {
    const [v, setV] = useState(0);
    const ref = useRef();
    useEffect(() => {
        const target = Number(value) || 0;
        const start = performance.now();
        const step = (t) => {
            const p = Math.min(1, (t - start) / 900);
            setV(Math.round((1 - Math.pow(1 - p, 3)) * target));
            if (p < 1) ref.current = requestAnimationFrame(step);
        };
        ref.current = requestAnimationFrame(step);
        return () => cancelAnimationFrame(ref.current);
    }, [value]);
    return <span className={className} style={style}>{v}</span>;
}



export default function Dashboard() {
    const navigate = useNavigate();
    const { regions, provincesOf } = useGeo();
    const [radarItems, setRadarItems] = useState([]);
    const [stats, setStats] = useState(null);
    const [areasCount, setAreasCount] = useState(0);
    const [analyses, setAnalyses] = useState([]);
    const [quota, setQuota] = useState(null);
    const [loading, setLoading] = useState(true);
    const [promoting, setPromoting] = useState(null);
    const [paywall, setPaywall] = useState(null);
    const [showAnalyses, setShowAnalyses] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [listRes, statsRes, areasRes, analysesRes, quotaRes] = await Promise.all([
                api.get("/radar/auctions", { params: { status: "screened", limit: 100 } }),
                api.get("/radar/stats"),
                api.get("/radar/areas"),
                api.get("/auctions"),
                api.get("/account/quota"),
            ]);
            setRadarItems(listRes.data || []);
            setStats(statsRes.data);
            setAreasCount((areasRes.data || []).length);
            setAnalyses(analysesRes.data || []);
            setQuota(quotaRes.data);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const top = [...radarItems]
        .sort((a, b) => (b.prescreening_score || 0) - (a.prescreening_score || 0))
        .slice(0, 4);
    const newest = [...radarItems]
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .slice(0, 5);

    const completed = analyses.filter((a) => a.status === "completed" && a.investment_score != null);
    const avgScore = completed.length
        ? Math.round(completed.reduce((s, a) => s + a.investment_score, 0) / completed.length)
        : null;
    const scoreTrend = [...completed]
        .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
        .slice(-14)
        .map((a) => ({ d: new Date(a.created_at).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" }), score: a.investment_score }));

    const analyze = async (it) => {
        setPromoting(it.id);
        try {
            const { data } = await api.post(`/radar/auctions/${it.id}/promote`);
            toast.success("Analisi approfondita avviata");
            navigate(`/auctions/${data.auction_id}`);
        } catch (err) {
            if (isPlanRequired(err)) setPaywall({ variant: "plan_required", source: "plan_required" });
            else if (isFreeQuotaExceeded(err)) setPaywall({ variant: "second_analysis_paywall", source: "second_analysis_paywall" });
            else toast.error(formatApiError(err.response?.data?.detail));
            setPromoting(null);
        }
    };

    const sw = scoreWord(avgScore);

    return (
        <Layout>
            {loading ? (
                <div className="space-y-6" data-testid="dashboard-loading">
                    <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
                        <div className="skeleton h-[320px] xl:col-span-2" />
                        <div className="skeleton h-[320px]" />
                    </div>
                    <div className="grid grid-cols-1 xl:grid-cols-5 gap-5">
                        <div className="skeleton h-72 xl:col-span-3" />
                        <div className="skeleton h-72 xl:col-span-2" />
                    </div>
                </div>
            ) : (
                <>
                    {/* ===== ROW 1: HERO + SCORE ===== */}
                    <div className="grid grid-cols-1 xl:grid-cols-3 gap-5 mb-5 fade-up" data-testid="dashboard-header">
                        {/* HERO */}
                        <div className="fd xl:col-span-2 relative rounded-2xl overflow-hidden border border-white/[0.09] min-h-[320px] flex flex-col justify-between bg-[#0A0F1E]">
                            <img src="/city-hero.jpg" alt="" className="absolute inset-y-0 right-0 w-[62%] h-full object-cover" />
                            <div className="absolute inset-0 bg-gradient-to-r from-[#0A0F1E] from-[38%] via-[#0A0F1E]/85 via-[55%] to-[#0A0F1E]/10" />
                            <div className="relative p-7 md:p-9 flex-1">
                                <h1 className="font-display-black text-[32px] sm:text-[38px] leading-[1.12] text-white max-w-md">
                                    Trasformiamo<br />i dati in opportunità.
                                </h1>
                                <div className="font-display-black text-[22px] sm:text-[26px] leading-tight mt-1 bg-gradient-to-r from-[#7C9AFF] to-[#A78BFA] bg-clip-text text-transparent">
                                    Tu investi, noi analizziamo.
                                </div>
                            </div>
                            <div className="relative flex flex-wrap gap-x-10 gap-y-4 px-7 md:px-9 pb-7" data-testid="dashboard-summary">
                                <HeroKpi icon={Buildings} iconColor="#A78BFA" value={stats?.total || 0} label="Aste disponibili" sub={`${stats?.by_category?.priorita_alta || 0} a priorità alta`} />
                                <HeroKpi icon={Brain} iconColor="#22D3EE" value={completed.length} label="Analisi completate" sub={areasCount ? `${areasCount} aree monitorate` : null} />
                                <HeroKpi icon={Trophy} iconColor="#4ADE80" value={stats?.by_category?.priorita_alta || 0} label="Opportunità ad alto score" sub={null} />
                            </div>
                        </div>

                        {/* INVESTMENT SCORE MEDIO */}
                        <div className="rounded-2xl border border-white/[0.09] bg-[#0B111F] p-6 flex flex-col" data-testid="dashboard-score-card">
                            <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-300 mb-4">Investment Score medio</div>
                            {avgScore != null ? (
                                <>
                                    <div className="flex items-center gap-4">
                                        <CountUp
                                            value={avgScore}
                                            className="font-mono-tight font-bold text-[64px] leading-none"
                                            style={{ color: sw.color, textShadow: `0 0 34px ${sw.color}45` }}
                                        />
                                        <div className="leading-tight">
                                            <div className="text-[15px] font-bold" style={{ color: sw.color }}>{sw.word}</div>
                                            <div className="text-xs text-slate-400 mt-0.5">su {completed.length} analisi</div>
                                        </div>
                                    </div>
                                    <div className="flex-1 min-h-[96px] mt-4 -mx-2">
                                        {scoreTrend.length > 1 && (
                                            <ResponsiveContainer width="100%" height={104}>
                                                <AreaChart data={scoreTrend} margin={{ top: 5, right: 8, left: 8, bottom: 0 }}>
                                                    <defs>
                                                        <linearGradient id="scoreGrad" x1="0" y1="0" x2="0" y2="1">
                                                            <stop offset="0%" stopColor="#4ADE80" stopOpacity={0.35} />
                                                            <stop offset="100%" stopColor="#4ADE80" stopOpacity={0} />
                                                        </linearGradient>
                                                    </defs>
                                                    <XAxis dataKey="d" hide />
                                                    <YAxis domain={[0, 100]} hide />
                                                    <Tooltip
                                                        contentStyle={{ background: "#0D1322", border: "1px solid rgba(148,178,255,0.2)", borderRadius: 8, fontSize: 11 }}
                                                        labelStyle={{ color: "#94A3B8" }}
                                                    />
                                                    <Area type="monotone" dataKey="score" stroke="#4ADE80" strokeWidth={2} dot={{ r: 2.5, fill: "#4ADE80" }} fill="url(#scoreGrad)" animationDuration={900} />
                                                </AreaChart>
                                            </ResponsiveContainer>
                                        )}
                                    </div>
                                </>
                            ) : (
                                <p className="text-sm text-slate-300 flex-1">Completa la prima analisi per vedere il tuo score medio.</p>
                            )}
                            <Link
                                to="/radar"
                                data-testid="score-card-radar-cta"
                                className="mt-4 h-12 flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#3B66F5] to-[#7C3AED] text-white text-sm font-bold hover:brightness-110 transition-all shadow-[0_6px_22px_rgba(79,90,245,0.35)]"
                            >
                                Vai al Radar <ArrowRight size={16} weight="bold" />
                            </Link>
                        </div>
                    </div>

                    {quota && !quota.unlimited && quota.remaining <= 0 && quota.plan !== "free" && <QuotaBar quota={quota} />}

                    {/* ===== ROW 2: TOP OPPORTUNITÀ + ANDAMENTO ===== */}
                    <div className="grid grid-cols-1 xl:grid-cols-5 gap-5 mb-5">
                        {/* TOP OPPORTUNITÀ DEL GIORNO */}
                        <div className="xl:col-span-3 rounded-2xl border border-white/[0.09] bg-[#0B111F] p-6" data-testid="section-top-opportunities">
                            <div className="flex items-center justify-between mb-5">
                                <div className="flex items-center gap-2">
                                    <Fire size={19} weight="fill" className="text-orange-400" />
                                    <h2 className="font-display text-[17px] font-bold text-white">Top Opportunità del Giorno</h2>
                                    <CaretRight size={14} weight="bold" className="text-slate-400" />
                                </div>
                                <Link to="/radar" data-testid="see-all-opportunities" className="text-xs font-semibold text-slate-200 border border-white/15 rounded-lg px-3 py-1.5 hover:border-white/35 hover:text-white transition-colors">
                                    Vedi tutte
                                </Link>
                            </div>
                            {top.length ? (
                                <div className="space-y-3" data-testid="opportunity-grid">
                                    {top.map((it) => (
                                        <OpportunityRow key={it.id} it={it} onAnalyze={analyze} analyzing={promoting === it.id} />
                                    ))}
                                </div>
                            ) : (
                                <EmptyHint />
                            )}
                        </div>

                        {/* ANDAMENTO ANALISI */}
                        <div className="xl:col-span-2 rounded-2xl border border-white/[0.09] bg-[#0B111F] p-6 flex flex-col" data-testid="section-performance">
                            <div className="flex items-center gap-2 mb-4">
                                <ChartLineUp size={18} weight="bold" className="text-[#A78BFA]" />
                                <h2 className="font-display text-[17px] font-bold text-white">Andamento Analisi</h2>
                            </div>
                            {completed.length > 2 ? (
                                <PerformancePanel analyses={completed} avgScore={avgScore} radarTotal={stats?.total || 0} />
                            ) : (
                                <p className="text-sm text-slate-300">Completa almeno 3 analisi per vedere l&apos;andamento.</p>
                            )}
                        </div>
                    </div>

                    {/* ===== ROW 3: AZIONI RAPIDE ===== */}
                    <div className="rounded-2xl border border-white/[0.09] bg-[#0B111F] p-6 mb-5" data-testid="section-quick-actions">
                        <div className="flex items-center gap-2 mb-5">
                            <Lightning size={18} weight="fill" className="text-amber-400" />
                            <h2 className="font-display text-[17px] font-bold text-white">Azioni Rapide</h2>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <QuickAction to="/new" testid="quick-new-analysis" icon={Brain} color="#A78BFA" label="Nuova Analisi" />
                            <QuickAction to="/compare" testid="quick-compare" icon={Scales} color="#22D3EE" label="Confronta Aste" />
                            <QuickAction to="/radar" testid="quick-radar" icon={Broadcast} color="#4ADE80" label="Radar Opportunità" />
                            <QuickAction to="/archive" testid="quick-archive" icon={ArchiveIcon} color="#7C9AFF" label="Archivio Analisi" />
                        </div>
                    </div>

                    {/* ===== NUOVE ASTE ===== */}
                    <section className="mb-5" data-testid="section-new-auctions">
                        <div className="rounded-2xl border border-white/[0.09] bg-[#0B111F] overflow-hidden">
                            <div className="flex items-center gap-2 px-6 pt-5 pb-3">
                                <Sparkle size={17} weight="fill" className="text-[#22D3EE]" />
                                <h2 className="font-display text-[17px] font-bold text-white">Nuove aste</h2>
                            </div>
                            {newest.length ? (
                                <div className="divide-y divide-white/[0.05]" data-testid="new-auctions-list">
                                    {newest.map((it) => <NewAuctionRow key={it.id} it={it} />)}
                                </div>
                            ) : (
                                <div className="px-6 pb-5"><EmptyHint /></div>
                            )}
                        </div>
                    </section>

                    {/* ===== AREE ===== */}
                    <section className="mb-5" data-testid="section-my-areas">
                        <MonitoredAreas
                            regions={regions}
                            provincesOf={provincesOf}
                            onChanged={load}
                            onPickArea={() => navigate("/radar")}
                        />
                    </section>

                    {/* ===== LE TUE ANALISI ===== */}
                    <section data-testid="section-my-analyses">
                        <button
                            onClick={() => setShowAnalyses((s) => !s)}
                            data-testid="toggle-my-analyses"
                            className="w-full flex items-center justify-between rounded-2xl border border-white/[0.09] bg-[#0B111F] px-6 py-4 hover:border-white/20 transition-colors"
                        >
                            <span className="flex items-center gap-2">
                                <Buildings size={18} weight="fill" className="text-[#A78BFA]" />
                                <span className="font-display text-[17px] font-bold text-white">Le tue analisi</span>
                                <span className="text-sm text-slate-400">({analyses.length})</span>
                            </span>
                            {showAnalyses ? <CaretUp size={16} weight="bold" /> : <CaretDown size={16} weight="bold" />}
                        </button>
                        {showAnalyses && (
                            analyses.length ? (
                                <AnalysesTable rows={analyses} />
                            ) : (
                                <p className="text-sm text-slate-400 px-6 py-6" data-testid="no-analyses-hint">
                                    Non hai ancora caricato analisi.{" "}
                                    <Link to="/new" className="underline hover:text-white">Crea la prima</Link>.
                                </p>
                            )
                        )}
                    </section>
                </>
            )}
            <StandardPaywallDialog open={!!paywall} onClose={() => setPaywall(null)}
                                   variant={paywall?.variant} source={paywall?.source} returnTo="/radar" />
        </Layout>
    );
}

const PLAN_LABEL = { free: "Free", standard: "Standard", pro: "Pro", business: "Pro", enterprise: "Admin" };

function HeroKpi({ icon: Icon, iconColor, value, label, sub }) {
    return (
        <div className="flex items-center gap-3.5">
            <span className="w-11 h-11 rounded-full flex items-center justify-center border" style={{ borderColor: iconColor + "55", background: iconColor + "14" }}>
                <Icon size={20} weight="duotone" style={{ color: iconColor }} />
            </span>
            <div className="leading-tight">
                <div className="font-mono-tight font-bold text-[26px] text-white leading-none">
                    <CountUp value={value} />
                </div>
                <div className="text-xs text-slate-300 mt-1">{label}</div>
                {sub && <div className="text-[11px] font-semibold text-emerald-400 mt-0.5">{sub}</div>}
            </div>
        </div>
    );
}

function QuickAction({ to, icon: Icon, color, label, testid }) {
    return (
        <Link
            to={to}
            data-testid={testid}
            className="flex flex-col items-center justify-center gap-3 rounded-xl border border-white/[0.09] bg-white/[0.02] py-6 hover:border-white/25 hover:bg-white/[0.05] hover:-translate-y-0.5 transition-all"
        >
            <span className="w-12 h-12 rounded-xl flex items-center justify-center border" style={{ borderColor: color + "55", background: color + "12" }}>
                <Icon size={22} weight="duotone" style={{ color }} />
            </span>
            <span className="text-[13px] font-semibold text-slate-100">{label}</span>
        </Link>
    );
}

function QuotaBar({ quota }) {
    const { plan, plan_label, limit, used, remaining, one_time, next_renewal } = quota;
    return (
        <div
            data-testid="dashboard-quota"
            className="rounded-2xl border border-amber-400/40 bg-amber-500/[0.07] px-6 py-5 mb-5 flex flex-col sm:flex-row sm:items-center gap-4"
        >
            <div className="flex items-center gap-3 shrink-0">
                <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-amber-500/20 text-amber-400">
                    <Gauge size={20} weight="fill" />
                </span>
                <div>
                    <div className="text-sm font-semibold text-white" data-testid="quota-remaining-label">
                        Analisi utilizzate: <span className="text-amber-400">{used} / {limit}</span> · rimanenti <span className="text-amber-400">{remaining}</span>
                    </div>
                    <div className="text-xs text-slate-300 mt-0.5" data-testid="quota-plan-line">
                        Piano {plan_label || PLAN_LABEL[plan] || plan}
                        {one_time && " · analisi una tantum"}
                        {next_renewal && ` · rinnovo il ${next_renewal.split("-").reverse().join("/")}`}
                    </div>
                </div>
            </div>
            <div className="flex-1" />
            <Link
                to="/piano"
                data-testid="quota-manage-plan-btn"
                className="shrink-0 h-11 px-5 inline-flex items-center justify-center gap-2 text-sm font-bold rounded-xl bg-gradient-to-r from-[#3B66F5] to-[#7C3AED] text-white hover:brightness-110 transition-all"
            >
                <Lightning size={15} weight="fill" /> Passa a un piano superiore
            </Link>
        </div>
    );
}

function OpportunityRow({ it, onAnalyze, analyzing }) {
    const tone = scoreTone(it.prescreening_score);
    const underMarket = it.market_position === "sotto_mercato" && it.market_discount_pct != null;
    const photo = it.photos?.[0];
    return (
        <div
            data-testid={`opportunity-card-${it.id}`}
            className="flex items-stretch gap-4 rounded-xl border border-white/[0.08] bg-white/[0.025] p-3.5 hover:border-white/20 hover:bg-white/[0.045] transition-all"
        >
            {/* Photo + score badge */}
            <div className="relative w-[104px] sm:w-[128px] shrink-0 rounded-lg overflow-hidden">
                <div className="absolute inset-0 grid-bg bg-gradient-to-br from-[#182240] to-[#0D1322] flex items-center justify-center">
                    <Buildings size={30} weight="duotone" className="text-slate-500" />
                </div>
                {photo && (
                    <img src={photo} alt="" className="absolute inset-0 w-full h-full object-cover" loading="lazy"
                        onError={(e) => { e.currentTarget.style.display = "none"; }} />
                )}
                <span
                    className="absolute top-1.5 left-1.5 min-w-[34px] h-[26px] px-1.5 rounded-md flex items-center justify-center font-mono-tight font-bold text-sm border backdrop-blur-md bg-[#04060B]/75"
                    style={{ color: tone.ring, borderColor: tone.ring + "88" }}
                >
                    {it.prescreening_score}
                </span>
            </div>
            {/* Info */}
            <div className="flex-1 min-w-0 py-0.5">
                <div className="font-semibold text-[15px] text-white capitalize truncate">
                    {propLabel(it.property_type)}{it.municipality ? ` — ${it.municipality} (${it.province || ""})` : ""}
                </div>
                <div className="text-xs text-slate-400 mt-0.5 flex items-center gap-1 truncate">
                    <MapPin size={11} /> {it.address || it.province || "—"}
                </div>
                <div className="flex items-center gap-4 mt-1.5 text-xs text-slate-300">
                    {it.surface_sqm && <span className="flex items-center gap-1"><Ruler size={12} /> {Math.round(it.surface_sqm)} m²</span>}
                    {it.rooms && <span className="flex items-center gap-1"><Buildings size={12} /> {it.rooms} locali</span>}
                    <OfferDeadlineBadge auctionDateIso={it.auction_date_iso} auctionDate={it.auction_date} testid={`opportunity-deadline-${it.id}`} />
                </div>
                <div className="flex items-baseline gap-6 mt-2">
                    <div>
                        <div className="text-[10px] uppercase tracking-wider text-slate-400">Prezzo base</div>
                        <div className="font-mono-tight font-bold text-[15px] text-white">{fmtEur(it.base_price)}</div>
                    </div>
                    {underMarket && (
                        <div>
                            <div className="text-[10px] uppercase tracking-wider text-slate-400">Sconto vs mercato</div>
                            <div className="font-mono-tight font-bold text-[15px] text-emerald-400">−{Math.round(it.market_discount_pct)}%</div>
                        </div>
                    )}
                </div>
            </div>
            {/* CTA */}
            <div className="flex items-center shrink-0">
                <button
                    onClick={() => onAnalyze(it)}
                    disabled={analyzing}
                    data-testid={`opportunity-analyze-${it.id}`}
                    className="h-10 px-5 inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#3B66F5] to-[#7C3AED] text-white text-sm font-bold rounded-lg hover:brightness-110 disabled:opacity-60 transition-all shadow-[0_4px_16px_rgba(79,90,245,0.3)]"
                >
                    {analyzing ? "Avvio…" : "Analizza"}
                </button>
            </div>
        </div>
    );
}

function PerformancePanel({ analyses, avgScore, radarTotal }) {
    const byMonth = {};
    analyses.forEach((a) => {
        const d = new Date(a.created_at);
        const k = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
        if (!byMonth[k]) byMonth[k] = { count: 0, sum: 0 };
        byMonth[k].count += 1;
        byMonth[k].sum += a.investment_score;
    });
    const data = Object.keys(byMonth).sort().slice(-8).map((k) => ({
        m: k.split("-").reverse().join("/"),
        analisi: byMonth[k].count,
        score: Math.round(byMonth[k].sum / byMonth[k].count),
    }));
    return (
        <div className="flex flex-col flex-1" data-testid="performance-chart">
            <div className="flex items-baseline gap-3 mb-1">
                <span className="font-mono-tight font-bold text-[32px] text-white leading-none">{analyses.length}</span>
                <span className="text-xs text-slate-300">analisi completate</span>
            </div>
            <div className="flex-1 min-h-[200px] -mx-2 mt-2">
                <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={data} margin={{ top: 5, right: 10, left: -22, bottom: 0 }}>
                        <defs>
                            <linearGradient id="perfPurple" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor="#8B5CF6" stopOpacity={0.4} />
                                <stop offset="100%" stopColor="#8B5CF6" stopOpacity={0} />
                            </linearGradient>
                        </defs>
                        <XAxis dataKey="m" tick={{ fill: "#8B99B0", fontSize: 11 }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fill: "#8B99B0", fontSize: 11 }} axisLine={false} tickLine={false} />
                        <Tooltip contentStyle={{ background: "#0D1322", border: "1px solid rgba(148,178,255,0.2)", borderRadius: 8, fontSize: 12 }} labelStyle={{ color: "#94A3B8" }} />
                        <Area type="monotone" dataKey="analisi" name="Analisi" stroke="#A78BFA" strokeWidth={2.5} dot={{ r: 3, fill: "#A78BFA" }} fill="url(#perfPurple)" animationDuration={900} />
                    </AreaChart>
                </ResponsiveContainer>
            </div>
            <div className="grid grid-cols-3 gap-px bg-white/[0.07] rounded-xl overflow-hidden border border-white/[0.07] mt-3">
                <PerfStat label="Score medio" value={avgScore != null ? String(avgScore) : "—"} color="#4ADE80" />
                <PerfStat label="Analisi totali" value={String(analyses.length)} color="#22D3EE" />
                <PerfStat label="Aste nel radar" value={String(radarTotal)} color="#A78BFA" />
            </div>
        </div>
    );
}

function PerfStat({ label, value, color }) {
    return (
        <div className="bg-[#0D1322] px-3 py-3 text-center">
            <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">{label}</div>
            <div className="font-mono-tight font-bold text-lg" style={{ color }}>{value}</div>
        </div>
    );
}

function NewAuctionRow({ it }) {
    const tone = scoreTone(it.prescreening_score);
    return (
        <Link
            to="/radar"
            data-testid={`new-auction-${it.id}`}
            className="flex items-center gap-4 px-6 py-3.5 hover:bg-white/[0.03] transition-colors"
        >
            <div className={`flex items-center justify-center w-10 h-10 rounded-lg shrink-0 ${tone.bg}`}>
                <span className={`font-mono-tight font-bold text-sm ${tone.text}`}>{it.prescreening_score}</span>
            </div>
            <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm text-white capitalize truncate">
                    {propLabel(it.property_type)}{it.municipality ? ` · ${it.municipality}` : ""}
                </div>
                <div className="text-xs text-slate-400 truncate flex items-center gap-1">
                    <MapPin size={11} /> {it.province || "—"}
                </div>
            </div>
            <div className="text-right shrink-0">
                <div className="font-mono-tight font-bold text-sm text-white">{fmtEur(it.base_price)}</div>
                <div className="text-[11px] text-slate-400">{new Date(it.created_at).toLocaleDateString("it-IT")}</div>
            </div>
            <ArrowRight size={15} weight="bold" className="text-slate-500 shrink-0" />
        </Link>
    );
}

function EmptyHint() {
    return (
        <div className="border border-dashed border-white/15 rounded-xl p-8 text-center text-sm text-slate-300" data-testid="empty-hint">
            Nessuna opportunità al momento.{" "}
            <Link to="/radar" className="underline hover:text-white">Apri il Radar</Link> per cercarne.
        </div>
    );
}

export function AnalysesTable({ rows }) {
    return (
        <div className="rounded-2xl border border-white/[0.09] bg-[#0B111F] overflow-x-auto mt-2" data-testid="auctions-table">
            <table className="w-full text-sm min-w-[640px]">
                <thead className="bg-white/[0.03] border-b border-white/[0.07] text-xs uppercase tracking-wider text-slate-300">
                    <tr>
                        <th className="text-left p-4 font-bold">Immobile</th>
                        <th className="text-left p-4 font-bold">Provincia</th>
                        <th className="text-left p-4 font-bold">Stato</th>
                        <th className="text-right p-4 font-bold">Score</th>
                        <th className="text-right p-4 font-bold">Data</th>
                        <th className="p-4 w-12"></th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((a) => {
                        const sc = scoreColor(a.investment_score);
                        const st = statusBadge(a.status);
                        const StIcon = st.icon;
                        return (
                            <tr key={a.id} data-testid={`auction-row-${a.id}`} className="border-b border-white/[0.04] last:border-0 hover:bg-white/[0.03] transition-colors">
                                <td className="p-4">
                                    <Link to={`/auctions/${a.id}`} className="block">
                                        <div className="font-semibold text-white">{a.title}</div>
                                        <div className="text-xs text-slate-400 mt-0.5 flex items-center gap-1">
                                            <MapPin size={11} />
                                            {a.address || a.comune || "Indirizzo non specificato"}
                                        </div>
                                        <div className="mt-1.5">
                                            <OfferDeadlineBadge
                                                auctionDateIso={a.auction_date_iso}
                                                auctionDate={a.auction_date}
                                                testid={`auction-deadline-${a.id}`}
                                            />
                                        </div>
                                    </Link>
                                </td>
                                <td className="p-4 text-sm text-slate-200">{a.province}</td>
                                <td className="p-4">
                                    <span className={`inline-flex items-center gap-1.5 border ${st.cls} px-2 py-1 text-[11px] font-bold uppercase tracking-wider rounded`}>
                                        <StIcon size={11} weight="bold" />
                                        {st.text}
                                    </span>
                                </td>
                                <td className="p-4 text-right">
                                    {a.investment_score != null ? (
                                        <div className="inline-flex items-center gap-2">
                                            <span className="font-mono-tight font-bold text-lg text-white">{a.investment_score}</span>
                                            <span className={`${sc.bg} ${sc.text} text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded`}>{sc.label}</span>
                                        </div>
                                    ) : (
                                        <span className="text-slate-500">—</span>
                                    )}
                                </td>
                                <td className="p-4 text-right text-xs text-slate-300 font-mono-tight">
                                    {new Date(a.created_at).toLocaleDateString("it-IT")}
                                </td>
                                <td className="p-4">
                                    <Link
                                        to={`/auctions/${a.id}`}
                                        data-testid={`auction-open-${a.id}`}
                                        className="inline-flex w-8 h-8 items-center justify-center rounded-lg border border-white/15 hover:border-[#8B5CF6]/60 hover:bg-[#8B5CF6]/15 transition-colors"
                                    >
                                        <ArrowRight size={14} weight="bold" />
                                    </Link>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}
